/**
 * Lead scoring and CRM integration system for Future Level
 */

import { SwissLanguage } from './languageTypes';

export type LeadSource = 'SEO' | 'LinkedIn' | 'Email' | 'Referral' | 'PR' | 'Direct';
export type MarketRegion = 'CH-DE' | 'CH-FR' | 'CH-IT' | 'CH-EN' | 'EU-DE' | 'EU-FR' | 'EU-IT' | 'EU-EN';
export type TargetCity = 'zurich' | 'basel' | 'bern' | 'geneva' | 'lausanne' | 'lugano' | 'winterthur' | 'luzern' | 'st-gallen' | 'stuttgart' | 'munich' | 'lyon' | 'paris' | 'milan' | 'como';
export type ServiceInterest = 'webdesign' | 'seo' | 'paid_ads' | 'ai_video' | 'ai_influencer' | '3d' | 'motion' | 'podcast' | 'uxui' | 'ecommerce' | 'arvr' | 'ai_copy' | 'automation' | 'email_marketing' | 'ai_music' | 'gbp' | 'reputation';
export type BundleInterest = 'launch_bundle' | 'ecommerce_scale' | 'prestige_visuals' | 'expansion_eu' | null;
export type BudgetBand = '≤3k' | '3-7k' | '7-15k' | '15-30k' | '30k+';
export type Timeline = 'ASAP' | '2-4w' | '1-3m' | '>3m';

export interface Lead {
  id: string;
  email: string;
  name?: string;
  company?: string;
  phone?: string;
  
  // Core classification
  lead_source: LeadSource;
  market_region: MarketRegion;
  target_city: TargetCity;
  service_interest: ServiceInterest;
  bundle_interest: BundleInterest;
  language: SwissLanguage;
  
  // Qualification data
  budget_band: BudgetBand;
  timeline: Timeline;
  lead_score: number;
  
  // Compliance
  consent_marketing: boolean;
  consent_timestamp?: string;
  
  // Attribution
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  
  // Tracking
  created_at: string;
  updated_at: string;
  first_touch_page?: string;
  last_touch_page?: string;
  session_count: number;
  
  // CRM sync
  crm_id?: string;
  crm_stage?: 'new' | 'discovery' | 'proposal' | 'won' | 'lost';
  assigned_to?: string;
}

/**
 * Lead scoring engine with transparent rules
 */
export class LeadScoring {
  
  /**
   * Calculate lead score based on actions and attributes
   */
  static calculateScore(actions: LeadAction[], lead: Partial<Lead>): number {
    let score = 0;
    
    // Action-based scoring
    actions.forEach(action => {
      switch (action.type) {
        case 'roi_calculator_submit':
          score += 8;
          break;
        case 'cost_calculator_submit':
          score += 6;
          break;
        case 'lead_form_started':
          if (action.context?.includes('city_service_page')) {
            score += 5;
          } else {
            score += 3;
          }
          break;
        case 'case_study_view':
          score += 4;
          break;
        case 'pdf_download':
          score += 3;
          break;
        case 'pricing_section_view':
          score += 2;
          break;
        case 'sticky_cta_click':
          score += 2;
          break;
        case 'blog_view':
          score += 1;
          break;
      }
    });
    
    // Budget-based scoring
    if (lead.budget_band) {
      switch (lead.budget_band) {
        case '30k+':
          score += 10;
          break;
        case '15-30k':
          score += 8;
          break;
        case '7-15k':
          score += 5;
          break;
        case '3-7k':
          score += 3;
          break;
        case '≤3k':
          score += 1;
          break;
      }
    }
    
    // Service-based scoring (high-value services)
    if (lead.service_interest) {
      const highValueServices: ServiceInterest[] = ['ecommerce', 'arvr', '3d', 'uxui', 'ai_influencer'];
      if (highValueServices.includes(lead.service_interest)) {
        score += 5;
      }
    }
    
    // Timeline urgency
    if (lead.timeline) {
      switch (lead.timeline) {
        case 'ASAP':
          score += 5;
          break;
        case '2-4w':
          score += 3;
          break;
        case '1-3m':
          score += 1;
          break;
      }
    }
    
    return Math.min(score, 50); // Cap at 50
  }
  
  /**
   * Determine if lead should be auto-qualified to SDR
   */
  static shouldAutoQualify(lead: Lead): boolean {
    const highValueServices: ServiceInterest[] = ['ecommerce', 'arvr', '3d', 'uxui'];
    const highBudgetBands: BudgetBand[] = ['7-15k', '15-30k', '30k+'];
    
    return (
      lead.lead_score >= 10 ||
      highBudgetBands.includes(lead.budget_band) ||
      highValueServices.includes(lead.service_interest)
    );
  }
  
  /**
   * Get market region from language and city
   */
  static getMarketRegion(language: SwissLanguage, city: TargetCity): MarketRegion {
    const swissCities: TargetCity[] = ['zurich', 'basel', 'bern', 'geneva', 'lausanne', 'lugano', 'winterthur', 'luzern', 'st-gallen'];
    
    if (swissCities.includes(city)) {
      return `CH-${language.toUpperCase()}` as MarketRegion;
    } else {
      return `EU-${language.toUpperCase()}` as MarketRegion;
    }
  }
  
  /**
   * Suggest bundle based on lead data
   */
  static suggestBundle(lead: Lead): BundleInterest {
    const { service_interest, budget_band, target_city } = lead;
    const swissCities: TargetCity[] = ['zurich', 'basel', 'bern', 'geneva', 'lausanne', 'lugano', 'winterthur', 'luzern', 'st-gallen'];
    
    // Prestige services
    if (['3d', 'arvr', 'ai_influencer', 'motion'].includes(service_interest)) {
      return 'prestige_visuals';
    }
    
    // E-commerce scale
    if (['ecommerce', 'paid_ads', 'email_marketing'].includes(service_interest)) {
      return 'ecommerce_scale';
    }
    
    // EU expansion
    if (!swissCities.includes(target_city)) {
      return 'expansion_eu';
    }
    
    // Default to launch bundle for smaller budgets
    if (['≤3k', '3-7k'].includes(budget_band)) {
      return 'launch_bundle';
    }
    
    return 'launch_bundle';
  }
}

/**
 * Lead action tracking interface
 */
export interface LeadAction {
  type: 'roi_calculator_submit' | 'cost_calculator_submit' | 'lead_form_started' | 'case_study_view' | 'pdf_download' | 'pricing_section_view' | 'sticky_cta_click' | 'blog_view';
  timestamp: string;
  context?: string;
  page_url?: string;
  session_id?: string;
}

/**
 * CRM integration utilities
 */
export class CRMIntegration {
  
  /**
   * Map lead data for CRM upsert
   */
  static mapLeadForCRM(lead: Lead): any {
    return {
      email: lead.email,
      name: lead.name,
      company: lead.company,
      phone: lead.phone,
      
      // Custom fields
      lead_source: lead.lead_source,
      market_region: lead.market_region,
      target_city: lead.target_city,
      service_interest: lead.service_interest,
      bundle_interest: lead.bundle_interest,
      language: lead.language,
      budget_band: lead.budget_band,
      timeline: lead.timeline,
      lead_score: lead.lead_score,
      
      // Attribution
      utm_source: lead.utm_source,
      utm_medium: lead.utm_medium,
      utm_campaign: lead.utm_campaign,
      utm_content: lead.utm_content,
      
      // Auto-assign by region
      owner: this.getOwnerByRegion(lead.market_region),
      
      // Deal stage
      stage: lead.crm_stage || 'new',
      
      // Tags
      tags: this.generateTags(lead)
    };
  }
  
  /**
   * Auto-assign owner by market region
   */
  private static getOwnerByRegion(region: MarketRegion): string {
    const assignments = {
      'CH-DE': 'swiss-de-team',
      'CH-FR': 'swiss-fr-team', 
      'CH-IT': 'swiss-it-team',
      'CH-EN': 'swiss-en-team',
      'EU-DE': 'eu-de-team',
      'EU-FR': 'eu-fr-team',
      'EU-IT': 'eu-it-team',
      'EU-EN': 'eu-en-team'
    };
    
    return assignments[region] || 'default-team';
  }
  
  /**
   * Generate CRM tags for lead
   */
  private static generateTags(lead: Lead): string[] {
    const tags = [];
    
    // Lead score tier
    if (lead.lead_score >= 20) tags.push('hot-lead');
    else if (lead.lead_score >= 10) tags.push('warm-lead');
    else tags.push('cold-lead');
    
    // Market region
    tags.push(lead.market_region.toLowerCase());
    
    // Service interest
    tags.push(`service-${lead.service_interest}`);
    
    // Budget tier
    if (['15-30k', '30k+'].includes(lead.budget_band)) {
      tags.push('high-budget');
    } else if (['7-15k'].includes(lead.budget_band)) {
      tags.push('medium-budget');
    } else {
      tags.push('low-budget');
    }
    
    // Timeline urgency
    if (['ASAP', '2-4w'].includes(lead.timeline)) {
      tags.push('urgent');
    }
    
    // Auto-qualification
    if (LeadScoring.shouldAutoQualify(lead)) {
      tags.push('auto-qualified');
    }
    
    return tags;
  }
  
  /**
   * Send lead to CRM via webhook/API
   */
  static async sendToCRM(lead: Lead, webhookUrl?: string): Promise<boolean> {
    try {
      const crmData = this.mapLeadForCRM(lead);
      
      if (webhookUrl) {
        // Send via Zapier/Make webhook
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'upsert_lead',
            data: crmData,
            timestamp: new Date().toISOString()
          })
        });
        
        return response.ok;
      } else {
        // Log for development
        console.log('CRM Lead Data:', crmData);
        return true;
      }
    } catch (error) {
      console.error('Failed to send lead to CRM:', error);
      return false;
    }
  }
}